import { type SkillSummary } from "@/shared/domain/skills";

import { type SkillSelection } from "../buildRequestTemplate";

/** Mirror of the backend ``MAX_AVAILABLE_SKILLS`` cap on a session's explicit
 *  skill set. Every attached skill becomes a line in the agent's LoadSkillTool
 *  registry, so an unbounded set bloats the per-turn prompt. */
export const MAX_AVAILABLE_SKILLS = 100;

/**
 * True when this skill is eligible to be attached to an agent.
 *
 * Mirrors the backend's ``_validate_agent_available_skills`` rule: any
 * org-shared tier (``org`` or ``org-editable``) is fair game — both have
 * org-wide visibility, only their edit surface differs. Private skills are
 * rejected because they would not resolve for any other org member who
 * launches the agent.
 */
export function isAgentEligibleSkill(summary: SkillSummary): boolean {
  return (
    summary.skill.accessibility === "org" ||
    summary.skill.accessibility === "org-editable"
  );
}

export interface SkillSelectionError {
  /** Index into ``FormState.skills``. ``-1`` marks a list-level error. */
  index: number;
  message: string;
}

/**
 * Pure validator for the agent's selected skills.
 *
 * ``eligibleSkills`` is the set of org-shared (``org`` or ``org-editable``)
 * skills currently visible to the author — pre-narrowed via
 * {@link isAgentEligibleSkill}. Call this only once that fetch has settled —
 * an unsettled (still empty) list would flag every selection as unavailable.
 *
 * Flags three conditions, each of which the backend also rejects:
 * - a ``skill_id`` that no longer resolves to a visible org-shared skill
 *   (deleted, or downgraded to private since it was attached);
 * - a duplicate ``skill_id`` (the per-session skill set must be unique);
 * - more than {@link MAX_AVAILABLE_SKILLS} entries.
 *
 * An empty selection is valid — the backend treats it as "no skills".
 */
export function validateSkills(
  skills: SkillSelection[],
  eligibleSkills: SkillSummary[],
): SkillSelectionError[] {
  const errors: SkillSelectionError[] = [];
  const available = new Set(eligibleSkills.map((s) => s.skill.skill_id));
  const seenAtRow = new Map<string, number>();
  skills.forEach((selection, index) => {
    if (!available.has(selection.skill_id)) {
      errors.push({
        index,
        message: "This skill is no longer available — remove it.",
      });
    }
    const previousRow = seenAtRow.get(selection.skill_id);
    if (previousRow !== undefined) {
      errors.push({
        index,
        message: `Duplicate of the skill at row ${previousRow + 1}.`,
      });
    } else {
      seenAtRow.set(selection.skill_id, index);
    }
  });
  if (skills.length > MAX_AVAILABLE_SKILLS) {
    errors.push({
      index: -1,
      message: `At most ${MAX_AVAILABLE_SKILLS} skills can be attached to an agent.`,
    });
  }
  return errors;
}

/** Convenience: true when no validator on the selection would block a save. */
export function allSkillsValid(
  skills: SkillSelection[],
  eligibleSkills: SkillSummary[],
): boolean {
  return validateSkills(skills, eligibleSkills).length === 0;
}
